import { createTheme } from "@shopify/restyle";
import normalize from "app/utils/normalize";
import { Platform } from "react-native";
import { boxVariants } from "./Box/Box";
import { buttonVariants } from "./Button/Button";
import { cardVariants } from "./Card/Card";
import palette from "./Colors";
import { body } from "./Text/Text";
import { textVariants } from "./Text/TextVariants";
import { title } from "./Text/Title";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";

const theme = createTheme({
  colors: {
    // ^^ Neutral section
    "neutral.900": palette.neutral[900],
    "neutral.800": palette.neutral[800],
    "neutral.700": palette.neutral[700],
    "neutral.600": palette.neutral[600],
    "neutral.500": palette.neutral[500],
    "neutral.400": palette.neutral[400],
    "neutral.350": palette.neutral[350],
    "neutral.300": palette.neutral[300],
    "neutral.200": palette.neutral[200],
    "neutral.100": palette.neutral[100],
    "neutral.75": palette.neutral[75],
    "neutral.50": palette.neutral[50],
    "neutral.white": palette.neutral.white,
    // ^^ Neutral transparent section
    "neutral.transparent.white.20": palette.neutral.transparent.white[20],
    "neutral.transparent.white.90": palette.neutral.transparent.white[90],
    "neutral.transparent.white.80": palette.neutral.transparent.white[80],
    // ^^ Neutral linear section
    "neutral.linear.white.start": palette.neutral.linear.white.start,
    "neutral.linear.white.end": palette.neutral.linear.white.end,

    // ^^ System section
    // ^^ Green section
    "system.green.900": palette.system.green[900],
    "system.green.500": palette.system.green[500],
    "system.green.100": palette.system.green[100],
    // ^^ Orange section
    "system.orange.900": palette.system.orange[900],
    "system.orange.500": palette.system.orange[500],
    "system.orange.100": palette.system.orange[100],
    // ^^ Red section
    "system.red.950": palette.system.red[950],
    "system.red.900": palette.system.red[900],
    "system.red.500": palette.system.red[500],
    "system.red.100": palette.system.red[100],

    // ^^ Brand section
    "brand.500": palette.brand[500],
    "brand.100": palette.brand[100],
    "brand.50": palette.brand[50],

    // ^^ Gradient section
    "gradient.blueGradient.start": palette.gradient.blueGradient.start,
    "gradient.blueGradient.end": palette.gradient.blueGradient.end,
    "gradient.greenGradient.start": palette.gradient.greenGradient.start,
    "gradient.greenGradient.end": palette.gradient.greenGradient.end,
    "gradient.orangeGradient.start": palette.gradient.orangeGradient.start,
    "gradient.orangeGradient.end": palette.gradient.orangeGradient.end,
    "gradient.greenToBlueGradient.start":
      palette.gradient.greenToBlueGradient.start,
    "gradient.greenToBlueGradient.end":
      palette.gradient.greenToBlueGradient.end,
    "gradient.reflectiveAnalysis.start":
      palette.gradient.reflectiveAnalysis.start,
    "gradient.reflectiveAnalysis.end": palette.gradient.reflectiveAnalysis.end,
    "gradient.ribon.start": palette.gradient.ribon.start,
    "gradient.ribon.end": palette.gradient.ribon.end,

    // ^^ Bubble section
    "bubble.bell.start": palette.bubble.bell.start,
    "bubble.bell.end": palette.bubble.bell.end,
    "bubble.pulse.start": palette.bubble.pulse.start,
    "bubble.pulse.end": palette.bubble.pulse.end,
    "bubble.checklist.start": palette.bubble.checklist.start,
    "bubble.checklist.end": palette.bubble.checklist.end,
    "bubble.survey.start": palette.bubble.survey.start,
    "bubble.survey.end": palette.bubble.survey.end,
    "bubble.coaching.start": palette.bubble.coaching.start,
    "bubble.coaching.end": palette.bubble.coaching.end,
    "bubble.resources.start": palette.bubble.resources.start,
    "bubble.resources.end": palette.bubble.resources.end,
    "bubble.review.start": palette.bubble.review.start,
    "bubble.review.end": palette.bubble.review.end,
    "bubble.like.start": palette.bubble.like.start,
    "bubble.like.end": palette.bubble.like.end,
    "bubble.comment.start": palette.bubble.comment.start,
    "bubble.comment.end": palette.bubble.comment.end,
    "bubble.message.start": palette.bubble.message.start,
    "bubble.message.end": palette.bubble.message.end,
    "bubble.other.start": palette.bubble.other.start,
    "bubble.other.end": palette.bubble.other.end,

    // TODO get read of all bellow
    shadow: isWebDesktop
      ? palette.hardGreyBlack
      : Platform.OS === "android"
      ? palette.neutral[500]
      : palette.greyBlack,

    transparent: "transparent",
  },
  breakpoints: {
    phone: 0,
    longPhone: {
      width: 0,
      height: 812,
    },
    tablet: 768,
    smallWeb: {
      width: 1024,
      height: 680,
    },
    web: {
      width: 1024,
      height: 770,
    },
    webMedium: 1440,
    webBig: 2560,
  },
  hitSlop: {
    top: 20,
    bottom: 20,
    left: 20,
    right: 20,
  },
  spacing: {
    0: normalize(0),
    null: normalize(0),
    2: normalize(2),
    4: 4,
    8: 8,
    xxs: normalize(4),
    xs: normalize(8),
    s: normalize(12),
    sm: normalize(16),
    m: normalize(20),
    xm: normalize(24),
    sl: normalize(28),
    l: normalize(32),
    xl: normalize(40),
    xxl: normalize(48),
    webM: normalize(56),
    webXM: normalize(64),
    webSL: normalize(72),
    webL: normalize(80),
    webXL: normalize(88),
    webXXL: normalize(96),
  },
  body: body,
  title: title,
  textVariants: textVariants,
  buttonVariants: buttonVariants,
  boxVariants: boxVariants,
  cardVariants: cardVariants,
});

export type Theme = typeof theme;
export default theme;
